/* ============================================================================
   SNAPGOODY — PERSONALISATION ENGINE
   ----------------------------------------------------------------------------
   One canvas engine, three shells:

     new SG.Editor(el, { product, mode: 'studio' })    Fitting Room page
     new SG.Editor(el, { product, mode: 'compact' })   Product Editor on a PDP
     new SG.Editor(el, { product, mode: 'overlay' })   layered designer

   Geometry is normalised (0–1 of the print area) so a design is resolution
   independent: the same layer stack renders to a 320px preview and to a
   300 DPI print file without any repositioning.

   Output
   ------
   editor.generate() resolves to a print package:
     { product, market, surfaces: { <id>: { widthMM, heightMM, dpi, px,
       background, layers[], printDataUrl, previewDataUrl } }, warnings[] }
   That object is what a Shopify line-item property + the Printify
   `print_areas` payload are built from. See PRINTIFY.md.
   ========================================================================== */
(function (global) {
  'use strict';
  var SG = global.SG;
  var $ = SG.$, $$ = SG.$$;

  /* ================================================================= FONTS ==*/
  var FONTS = [
    { id: 'cormorant', label: 'Editorial', css: '"Cormorant Garamond", Georgia, serif', sample: 'Ashwood' },
    { id: 'jost',      label: 'Modern',    css: '"Jost", Helvetica, Arial, sans-serif', sample: 'Ashwood' },
    { id: 'marcellus', label: 'Classic',   css: '"Marcellus", Georgia, serif',          sample: 'Ashwood' },
    { id: 'italiana',  label: 'Fashion',   css: '"Italiana", Georgia, serif',           sample: 'Ashwood' },
    { id: 'parisienne',label: 'Script',    css: '"Parisienne", cursive',                sample: 'Ashwood' }
  ];
  function fontCss(id) {
    for (var i = 0; i < FONTS.length; i++) if (FONTS[i].id === id) return FONTS[i].css;
    return FONTS[0].css;
  }

  var INK_SWATCHES = ['#FFFFFF', '#1C1815', '#A9694C', '#8B5238', '#6C6B54', '#B9A78F', '#D9CEC0', '#4A6670'];
  var BG_SWATCHES  = ['#FFFFFF', '#F6F0E8', '#EFE8DD', '#1C1815', '#3B342C', '#A9694C', '#6C6B54', '#C9B9A4'];

  var FILTERS = [
    { id: 'none',  label: 'None',  css: 'none' },
    { id: 'warm',  label: 'Warm',  css: 'saturate(1.08) sepia(0.16) contrast(1.03)' },
    { id: 'mono',  label: 'Mono',  css: 'grayscale(1) contrast(1.06)' },
    { id: 'soft',  label: 'Soft',  css: 'saturate(0.82) brightness(1.05) contrast(0.94)' },
    { id: 'deep',  label: 'Deep',  css: 'contrast(1.16) saturate(1.12) brightness(0.97)' }
  ];
  function filterCss(id) {
    for (var i = 0; i < FILTERS.length; i++) if (FILTERS[i].id === id) return FILTERS[i].css;
    return 'none';
  }

  /* ============================================================= TEMPLATES ==*/
  var TEMPLATES = [
    { id: 'full',    label: 'Full bleed',   svg: '<rect x="2" y="2" width="96" height="96" fill="#D5C8B6"/>' },
    { id: 'inset',   label: 'Inset frame',  svg: '<rect x="2" y="2" width="96" height="96" fill="#FBF9F6"/><rect x="14" y="14" width="72" height="72" fill="#D5C8B6"/>' },
    { id: 'caption', label: 'Photo + words',svg: '<rect x="2" y="2" width="96" height="96" fill="#FBF9F6"/><rect x="10" y="10" width="80" height="58" fill="#D5C8B6"/><rect x="24" y="76" width="52" height="4" fill="#A9694C"/>' },
    { id: 'band',    label: 'Lower band',   svg: '<rect x="2" y="2" width="96" height="96" fill="#D5C8B6"/><rect x="2" y="66" width="96" height="32" fill="#1C1815" opacity=".82"/><rect x="16" y="78" width="46" height="4" fill="#FBF9F6"/>' },
    { id: 'stack',   label: 'Words only',   svg: '<rect x="2" y="2" width="96" height="96" fill="#F6F0E8"/><rect x="20" y="38" width="60" height="6" fill="#1C1815"/><rect x="30" y="52" width="40" height="4" fill="#A9694C"/>' },
    { id: 'duo',     label: 'Split',        svg: '<rect x="2" y="2" width="96" height="96" fill="#FBF9F6"/><rect x="2" y="2" width="48" height="96" fill="#D5C8B6"/><rect x="60" y="42" width="28" height="4" fill="#1C1815"/>' }
  ];

  /* ================================================================= UTILS ==*/
  function rad(d) { return d * Math.PI / 180; }
  function nowId() { return 'l' + Math.random().toString(36).slice(2, 9); }

  function loadImage(src) {
    return new Promise(function (res, rej) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { res(img); };
      img.onerror = rej;
      img.src = src;
    });
  }

  /* ================================================================ EDITOR ==*/
  function Editor(mount, opts) {
    opts = opts || {};
    this.mount = typeof mount === 'string' ? $(mount) : mount;
    this.mode = opts.mode || 'studio';
    this.onChange = opts.onChange || function () {};
    this.onDone = opts.onDone || null;
    this.doneLabel = opts.doneLabel || 'Save design';
    this.allowProductSwitch = opts.allowProductSwitch !== false && this.mode === 'studio';
    this.zoom = 1;
    this.showGuides = true;
    this.selected = null;
    this.history = [];
    this.future = [];
    this.designs = {};       // surfaceId -> { layers, bg }
    this.options = {};
    this.setProduct(opts.product || SG.catalog.products[0], opts.design);
    this.render();
    (SG.__editors || (SG.__editors = [])).push(this);
  }

  Editor.FONTS = FONTS;
  Editor.TEMPLATES = TEMPLATES;

  Editor.prototype.setProduct = function (product, design) {
    var self = this;
    this.product = product;
    /* Surfaces and options are read through the catalog so that a product
       whose print size or choices differ by market resolves correctly. */
    this.surfaces = SG.catalog.surfacesFor(product, SG.region) || [];
    this.surfaceId = (this.surfaces[0] || {}).id || 'front';
    this.designs = {};
    this.options = {};
    SG.catalog.optionsFor(product, SG.region).forEach(function (o) {
      self.options[o.name] = o.values[0];
    });
    this.surfaces.forEach(function (s) {
      self.designs[s.id] = { layers: [], bg: SG.catalog.surfaceBg(s, self.options) || s.bg || '#FFFFFF' };
    });
    if (design) this.load(design);
    this.selected = null;
    this.history = []; this.future = [];
  };

  /* A face whose background comes from a product option (leather colour, wallet
     colour) is not the customer's to choose — the background IS the material. */
  Editor.prototype.bgLocked = function (id) {
    var s = this.surfaceById(id || this.surfaceId);
    return !!(s && s.bgFrom) || this.product.allowBackground === false;
  };

  Editor.prototype.surfaceById = function (id) {
    for (var i = 0; i < this.surfaces.length; i++) if (this.surfaces[i].id === id) return this.surfaces[i];
    return null;
  };

  /* Re-paint every option-driven background after an option changes. */
  Editor.prototype.applyOptionBackgrounds = function () {
    var self = this, changed = false;
    this.surfaces.forEach(function (s) {
      var bg = SG.catalog.surfaceBg(s, self.options);
      if (bg && self.designs[s.id] && self.designs[s.id].bg !== bg) {
        self.designs[s.id].bg = bg;
        changed = true;
      }
    });
    if (changed) {
      this.el.print.style.setProperty('--art-bg', this.design().bg);
      this.draw();
    }
  };

  /* Market changed — re-resolve surfaces and options, keeping what we can. */
  Editor.prototype.refreshForMarket = function () {
    var keep = this.snapshot();
    this.setProduct(this.product);
    var self = this;
    Object.keys(keep.designs).forEach(function (id) {
      if (self.designs[id]) {
        self.designs[id] = {
          bg: SG.catalog.surfaceBg(self.surfaceById(id), self.options) || keep.designs[id].bg,
          layers: keep.designs[id].layers.map(function (l) { return self.rehydrate(l); })
        };
      }
    });
    this.refreshMock(); this.paintPanes(); this.fit(); this.draw();
  };

  Editor.prototype.surface = function () {
    var id = this.surfaceId, s = this.surfaces;
    for (var i = 0; i < s.length; i++) if (s[i].id === id) return s[i];
    return s[0] || { widthMM: 100, heightMM: 100, dpi: 300, area: { x: .1, y: .1, w: .8, h: .8 }, safe: .06 };
  };
  Editor.prototype.design = function () {
    return this.designs[this.surfaceId] || (this.designs[this.surfaceId] = { layers: [], bg: '#FFFFFF' });
  };
  Editor.prototype.layers = function () { return this.design().layers; };

  /* ========================================================== MARKUP SHELL ==*/
  Editor.prototype.render = function () {
    var self = this;
    var compact = this.mode !== 'studio';

    this.mount.innerHTML =
      '<div class="studio' + (compact ? ' studio--compact' : '') + (this.mode === 'overlay' ? ' studio--overlay' : '') + '">' +
        '<div class="studio__rail">' +
          '<div class="studio__tabs" data-tabs></div>' +
          '<div class="studio__panes scroll-y" data-panes></div>' +
          (compact ? '<div class="studio__actions" data-actions></div>' : '') +
        '</div>' +
        '<div class="studio__stage">' +
          '<div class="stage__bar" data-bar></div>' +
          '<div class="stage__viewport" data-viewport>' +
            '<div class="stage__scene" data-scene>' +
              '<div class="stage__mock" data-mock>' +
                '<div data-mockmedia></div>' +
                '<div class="stage__print" data-print><canvas data-canvas></canvas></div>' +
                '<div class="stage__guides" data-guides>' +
                  '<div class="guide-bleed"></div><div class="guide-safe"></div>' +
                  '<div class="guide-center-x"></div><div class="guide-center-y"></div>' +
                '</div>' +
                '<div class="stage__handles" data-handles></div>' +
                '<div class="stage__mockshade"></div>' +
              '</div>' +
            '</div>' +
            '<div class="stage__empty" data-empty><div>' + SG.icon('sparkle') +
              '<strong>Your design starts here</strong>' +
              '<span>Upload a photograph or add a line of text</span></div></div>' +
          '</div>' +
          '<div class="exportview" data-exportview></div>' +
        '</div>' +
        (compact ? '' :
          '<div class="studio__panel">' +
            '<div class="studio__panelhead"><h4>Layers</h4>' +
              '<div class="layer__acts">' +
                '<button type="button" data-add-text title="Add text">' + SG.icon('type') + '</button>' +
                '<button type="button" data-add-photo title="Add photo">' + SG.icon('image') + '</button>' +
              '</div>' +
            '</div>' +
            '<div class="layers scroll-y" data-layers></div>' +
            '<div class="props scroll-y" data-props></div>' +
            '<div class="summary" data-summary></div>' +
            '<div class="studio__actions" data-actions></div>' +
          '</div>') +
      '</div>';

    this.el = {
      root: $('.studio', this.mount),
      tabs: $('[data-tabs]', this.mount),
      panes: $('[data-panes]', this.mount),
      bar: $('[data-bar]', this.mount),
      viewport: $('[data-viewport]', this.mount),
      scene: $('[data-scene]', this.mount),
      mock: $('[data-mock]', this.mount),
      mockmedia: $('[data-mockmedia]', this.mount),
      print: $('[data-print]', this.mount),
      canvas: $('[data-canvas]', this.mount),
      guides: $('[data-guides]', this.mount),
      handles: $('[data-handles]', this.mount),
      empty: $('[data-empty]', this.mount),
      layers: $('[data-layers]', this.mount),
      props: $('[data-props]', this.mount),
      summary: $('[data-summary]', this.mount),
      actions: $('[data-actions]', this.mount),
      exportview: $('[data-exportview]', this.mount)
    };
    this.ctx = this.el.canvas.getContext('2d');

    this.buildTabs();
    this.buildBar();
    this.buildActions();
    this.bindStage();
    this.refreshMock();

    if (!this._resizeBound) {
      this._resizeBound = SG.debounce(function () { self.fit(); }, 90);
      global.addEventListener('resize', this._resizeBound);
    }
    if (global.document.fonts && global.document.fonts.ready) {
      global.document.fonts.ready.then(function () { self.draw(); self.syncHandles(); });
    }
    this.history = [this.snapshot()];
    requestAnimationFrame(function () { self.fit(); });
  };

  /* ================================================================== TABS ==*/
  Editor.prototype.buildTabs = function () {
    var self = this;
    var tabs = [
      { id: 'photo',  label: 'Photo' },
      { id: 'text',   label: 'Text' },
      { id: 'layout', label: 'Layout' }
    ];
    if (this.mode !== 'studio') tabs.push({ id: 'layers', label: 'Layers' });
    if (this.allowProductSwitch) tabs.unshift({ id: 'product', label: 'Product' });

    this.el.tabs.innerHTML = tabs.map(function (t, i) {
      return '<button class="studio__tab' + (i === 0 ? ' is-active' : '') + '" type="button" data-tab="' + t.id + '">' + t.label + '</button>';
    }).join('');
    this.el.panes.innerHTML = tabs.map(function (t, i) {
      return '<div class="studio__pane' + (i === 0 ? ' is-active' : '') + '" data-pane="' + t.id + '"></div>';
    }).join('');

    $$('[data-tab]', this.el.tabs).forEach(function (b) {
      b.addEventListener('click', function () {
        $$('[data-tab]', self.el.tabs).forEach(function (x) { x.classList.remove('is-active'); });
        b.classList.add('is-active');
        $$('[data-pane]', self.el.panes).forEach(function (p) {
          p.classList.toggle('is-active', p.getAttribute('data-pane') === b.getAttribute('data-tab'));
        });
      });
    });

    this.paintPanes();
  };

  Editor.prototype.pane = function (id) { return $('[data-pane="' + id + '"]', this.el.panes); };

  Editor.prototype.paintPanes = function () {
    if (this.allowProductSwitch) this.paintProductPane();
    this.paintPhotoPane();
    this.paintTextPane();
    this.paintLayoutPane();
    if (this.mode !== 'studio') this.paintLayersPane();
    this.refreshLayers();
    this.refreshProps();
    this.refreshSummary();
  };

  /* --------------------------------------------------------- product pane --*/
  Editor.prototype.paintProductPane = function () {
    var self = this;
    var host = this.pane('product'); if (!host) return;
    var personalisable = SG.catalog.products.filter(function (p) { return p.personalisable; });

    host.innerHTML =
      '<div class="studio__group">' +
        '<div class="studio__grouphead"><h4>Choose a product</h4></div>' +
        '<div class="field"><select class="select" data-product>' +
          personalisable.map(function (p) {
            return '<option value="' + p.handle + '"' + (p.handle === self.product.handle ? ' selected' : '') + '>' + SG.esc(p.title) + '</option>';
          }).join('') +
        '</select></div>' +
        '<p class="studio__hint">Switching product keeps your layers and re-fits them to the new print area.</p>' +
      '</div>' +
      '<div class="studio__group" data-options></div>';

    $('[data-product]', host).addEventListener('change', function (e) {
      var keep = JSON.parse(JSON.stringify(self.serialiseSurface()));
      self.setProduct(SG.catalog.get(e.target.value));
      self.design().layers = keep.layers.map(function (l) { return self.rehydrate(l); });
      self.design().bg = keep.bg;
      self.refreshMock(); self.paintPanes(); self.fit(); self.commit();
      SG.emit('editor:product', self.product);
    });

    this.paintOptions($('[data-options]', host));
  };

  Editor.prototype.paintOptions = function (host) {
    if (!host) return;
    var self = this;
    var opts = SG.catalog.optionsFor(this.product, SG.region) || [];
    if (!opts.length) { host.innerHTML = ''; return; }
    host.innerHTML = '<div class="studio__grouphead"><h4>Options</h4></div>' +
      opts.map(function (o) {
        return '<div class="ctl"><span class="ctl__label">' + SG.esc(o.name) + '</span>' +
          '<select class="select" data-opt="' + SG.esc(o.name) + '">' +
            o.values.map(function (v) {
              return '<option' + (self.options[o.name] === v ? ' selected' : '') + '>' + SG.esc(v) + '</option>';
            }).join('') +
          '</select></div>';
      }).join('');
    $$('[data-opt]', host).forEach(function (s) {
      s.addEventListener('change', function () {
        self.options[s.getAttribute('data-opt')] = s.value;
        self.applyOptionBackgrounds();
        self.paintPhotoPane();
        self.refreshSummary();
        self.onChange(self);
      });
    });
  };

  /* ----------------------------------------------------------- photo pane --*/
  Editor.prototype.paintPhotoPane = function () {
    var self = this;
    var host = this.pane('photo');
    host.innerHTML =
      '<div class="studio__group">' +
        '<label class="dropzone" data-drop>' +
          SG.icon('upload') +
          '<strong>Upload a photograph</strong>' +
          '<span>JPG, PNG or HEIC · up to 25MB</span>' +
          '<input type="file" accept="image/*" multiple data-file>' +
        '</label>' +
        '<p class="studio__hint">For the sharpest print, use the original file straight from your camera or phone rather than a screenshot or a download from social media.</p>' +
      '</div>' +
      '<div class="studio__group">' +
        '<div class="studio__grouphead"><h4>Treatment</h4></div>' +
        '<div class="seg" data-filters>' +
          FILTERS.map(function (f, i) {
            return '<button type="button" data-filter="' + f.id + '"' + (i === 0 ? ' class="is-active"' : '') + '>' + f.label + '</button>';
          }).join('') +
        '</div>' +
        '<p class="studio__hint">Applies to the selected photograph.</p>' +
      '</div>' +
      (this.bgLocked()
        ? '<div class="studio__group">' +
            '<div class="studio__grouphead"><h4>Background</h4></div>' +
            '<p class="studio__hint">' + (this.product.allowBackground === false
              ? 'This piece prints on its own surface — there is no background colour to choose.'
              : 'The background is the material colour, set by the options above.') + '</p>' +
          '</div>'
        : '<div class="studio__group">' +
            '<div class="studio__grouphead"><h4>Print background</h4></div>' +
            '<div class="swatches" data-bg></div>' +
          '</div>');

    var input = $('[data-file]', host);
    var drop = $('[data-drop]', host);
    input.addEventListener('change', function (e) { self.addFiles(e.target.files); input.value = ''; });
    ['dragenter', 'dragover'].forEach(function (t) {
      drop.addEventListener(t, function (e) { e.preventDefault(); drop.classList.add('is-over'); });
    });
    ['dragleave', 'drop'].forEach(function (t) {
      drop.addEventListener(t, function (e) { e.preventDefault(); drop.classList.remove('is-over'); });
    });
    drop.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files) self.addFiles(e.dataTransfer.files);
    });

    $$('[data-filter]', host).forEach(function (b) {
      b.addEventListener('click', function () {
        var l = self.selectedLayer();
        if (!l || l.type !== 'image') { SG.toast('Select a photograph first.', 'info'); return; }
        $$('[data-filter]', host).forEach(function (x) { x.classList.remove('is-active'); });
        b.classList.add('is-active');
        l.filter = b.getAttribute('data-filter');
        self.draw(); self.commit();
      });
    });

    var bgHost = $('[data-bg]', host);
    if (bgHost) this.paintSwatches(bgHost, BG_SWATCHES, this.design().bg, function (c) {
      self.design().bg = c;
      self.el.print.style.setProperty('--art-bg', c);
      self.draw(); self.commit();
    });
  };

  /* ------------------------------------------------------------ text pane --*/
  Editor.prototype.paintTextPane = function () {
    var self = this;
    var host = this.pane('text');
    host.innerHTML =
      '<div class="studio__group">' +
        '<div class="field">' +
          '<span class="field__label">Your words</span>' +
          '<textarea class="textarea" rows="3" placeholder="Ashwood Lane&#10;August 2026" data-newtext></textarea>' +
        '</div>' +
        '<button class="btn btn--block" type="button" data-addtext style="margin-top:.7rem">' + SG.icon('plus') + 'Add to design</button>' +
      '</div>' +
      '<div class="studio__group">' +
        '<div class="studio__grouphead"><h4>Typeface</h4></div>' +
        '<div class="fontlist" data-fonts>' +
          FONTS.map(function (f, i) {
            return '<button class="fontbtn' + (i === 0 ? ' is-active' : '') + '" type="button" data-font="' + f.id + '">' +
              '<em style="font-family:' + f.css + '">' + f.sample + '</em><span>' + f.label + '</span></button>';
          }).join('') +
        '</div>' +
      '</div>' +
      '<div class="studio__group">' +
        '<div class="studio__grouphead"><h4>Ink</h4></div>' +
        '<div class="swatches" data-ink></div>' +
      '</div>';

    var addBtn = $('[data-addtext]', host);
    var ta = $('[data-newtext]', host);
    addBtn.addEventListener('click', function () {
      var v = (ta.value || '').trim();
      self.addText(v || 'Your words here');
      ta.value = '';
    });
    ta.addEventListener('keydown', function (e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') addBtn.click();
    });

    $$('[data-font]', host).forEach(function (b) {
      b.addEventListener('click', function () {
        $$('[data-font]', host).forEach(function (x) { x.classList.remove('is-active'); });
        b.classList.add('is-active');
        self.defaultFont = b.getAttribute('data-font');
        var l = self.selectedLayer();
        if (l && l.type === 'text') { l.font = self.defaultFont; self.draw(); self.commit(); }
      });
    });

    this.paintSwatches($('[data-ink]', host), INK_SWATCHES, '#1C1815', function (c) {
      self.defaultInk = c;
      var l = self.selectedLayer();
      if (l && l.type === 'text') { l.color = c; self.draw(); self.commit(); }
    });
  };

  /* ---------------------------------------------------------- layout pane --*/
  Editor.prototype.paintLayoutPane = function () {
    var self = this;
    var host = this.pane('layout');
    var surfaces = this.surfaces;
    host.innerHTML =
      (surfaces.length > 1 ?
        '<div class="studio__group">' +
          '<div class="studio__grouphead"><h4>' + (surfaces.length > 4 ? 'Month' : 'Print area') + '</h4>' +
            '<span style="font-size:.6rem;letter-spacing:.1em;color:var(--ink-4)" data-surface-count></span></div>' +
          /* A handful of faces read best as a segmented control; twelve months
             need a list. */
          (surfaces.length > 4
            ? '<select class="select" data-surface-select>' +
                surfaces.map(function (s, i) {
                  return '<option value="' + s.id + '"' + (s.id === self.surfaceId ? ' selected' : '') + '>' +
                    (i + 1) + ' · ' + SG.esc(s.label) + '</option>';
                }).join('') +
              '</select>' +
              '<div class="surfacegrid" data-surfacegrid>' +
                surfaces.map(function (s, i) {
                  return '<button type="button" data-surface="' + s.id + '"' +
                    (s.id === self.surfaceId ? ' class="is-active"' : '') +
                    ' title="' + SG.esc(s.label) + '">' + SG.esc(s.label.slice(0, 3)) + '</button>';
                }).join('') +
              '</div>'
            : '<div class="seg" data-surfaces>' +
                surfaces.map(function (s) {
                  return '<button type="button" data-surface="' + s.id + '"' + (s.id === self.surfaceId ? ' class="is-active"' : '') + '>' + SG.esc(s.label) + '</button>';
                }).join('') +
              '</div>') +
          '<p class="studio__hint">' + (surfaces.length > 4
            ? 'Every month is designed separately and prints as its own file. Anything you have not touched prints on the background alone.'
            : 'This product has more than one printed face. Each keeps its own design.') + '</p>' +
        '</div>' : '') +
      '<div class="studio__group">' +
        '<div class="studio__grouphead"><h4>Quick layouts</h4></div>' +
        '<div class="tmplgrid" data-templates>' +
          TEMPLATES.map(function (t) {
            return '<button class="tmpl" type="button" data-tmpl="' + t.id + '" title="' + t.label + '">' +
              '<svg viewBox="0 0 100 100">' + t.svg + '</svg></button>';
          }).join('') +
        '</div>' +
        '<p class="studio__hint">Layouts arrange what you have already added — nothing is thrown away.</p>' +
      '</div>' +
      '<div class="studio__group">' +
        '<div class="studio__grouphead"><h4>Print guides</h4></div>' +
        '<div class="seg"><button type="button" data-guides-on class="is-active">Show</button><button type="button" data-guides-off>Hide</button></div>' +
        '<p class="studio__hint">The dashed line is the safe area. Keep faces and words inside it — anything outside may be trimmed.</p>' +
      '</div>';

    function gotoSurface(id) {
      self.surfaceId = id;
      self.selected = null;
      $$('[data-surface]', host).forEach(function (x) {
        x.classList.toggle('is-active', x.getAttribute('data-surface') === id);
      });
      var sel = $('[data-surface-select]', host);
      if (sel && sel.value !== id) sel.value = id;
      self.refreshMock(); self.fit(); self.draw();
      self.refreshLayers(); self.refreshProps(); self.refreshSummary();
      self.markDesignedSurfaces();
      self.paintPhotoPane();
    }
    $$('[data-surface]', host).forEach(function (b) {
      b.addEventListener('click', function () { gotoSurface(b.getAttribute('data-surface')); });
    });
    var ssel = $('[data-surface-select]', host);
    if (ssel) ssel.addEventListener('change', function () { gotoSurface(ssel.value); });
    this.markDesignedSurfaces();
    $$('[data-tmpl]', host).forEach(function (b) {
      b.addEventListener('click', function () { self.applyTemplate(b.getAttribute('data-tmpl')); });
    });
    var on = $('[data-guides-on]', host), off = $('[data-guides-off]', host);
    on.addEventListener('click', function () { on.classList.add('is-active'); off.classList.remove('is-active'); self.showGuides = true; self.el.guides.classList.remove('is-hidden'); });
    off.addEventListener('click', function () { off.classList.add('is-active'); on.classList.remove('is-active'); self.showGuides = false; self.el.guides.classList.add('is-hidden'); });
  };

  /* ---------------------------------------------------------- layers pane --*/
  Editor.prototype.paintLayersPane = function () {
    var host = this.pane('layers');
    if (!host) return;
    host.innerHTML =
      '<div class="studio__group">' +
        '<div class="studio__grouphead"><h4>Layers</h4>' +
          '<div class="layer__acts">' +
            '<button type="button" data-add-text title="Add text">' + SG.icon('type') + '</button>' +
            '<button type="button" data-add-photo title="Add photo">' + SG.icon('image') + '</button>' +
          '</div>' +
        '</div>' +
        '<div class="layers" data-layers-c></div>' +
      '</div>' +
      '<div class="studio__group"><div class="props" data-props-c style="padding:0;border:0"></div></div>' +
      '<div class="studio__group"><div class="summary" data-summary-c style="padding:0;border:0;background:none"></div></div>';
    this.el.layers = $('[data-layers-c]', host);
    this.el.props = $('[data-props-c]', host);
    this.el.summary = $('[data-summary-c]', host);
    this.bindAddButtons(host);
  };

  Editor.prototype.paintSwatches = function (host, list, active, fn) {
    if (!host) return;
    host.innerHTML = list.map(function (c) {
      return '<button class="swatch' + (c === active ? ' is-active' : '') + '" type="button" data-c="' + c + '" style="background:' + c + '" aria-label="' + c + '"></button>';
    }).join('') +
      '<label class="swatch swatch--custom" title="Custom colour">' + SG.icon('palette') + '<input type="color" value="' + (active || '#A9694C') + '"></label>';
    $$('.swatch[data-c]', host).forEach(function (b) {
      b.addEventListener('click', function () {
        $$('.swatch', host).forEach(function (x) { x.classList.remove('is-active'); });
        b.classList.add('is-active');
        fn(b.getAttribute('data-c'));
      });
    });
    var custom = $('input[type="color"]', host);
    if (custom) custom.addEventListener('input', function () { fn(custom.value); });
  };

  /* ============================================================== TOOL BAR ==*/
  Editor.prototype.buildBar = function () {
    var self = this;
    this.el.bar.innerHTML =
      '<button class="tool tool--icon" type="button" data-undo title="Undo">' + SG.icon('undo') + '</button>' +
      '<button class="tool tool--icon" type="button" data-redo title="Redo">' + SG.icon('redo') + '</button>' +
      '<span style="width:1px;height:18px;background:var(--line);margin:0 .25rem"></span>' +
      '<button class="tool tool--icon" type="button" data-dupe title="Duplicate">' + SG.icon('copy') + '</button>' +
      '<button class="tool tool--icon" type="button" data-del title="Delete">' + SG.icon('trash') + '</button>' +
      '<button class="tool tool--icon" type="button" data-centre title="Centre">' + SG.icon('center') + '</button>' +
      '<span class="spacer"></span>' +
      '<span class="stage__zoom">' +
        '<button class="tool tool--icon" type="button" data-zout title="Zoom out">' + SG.icon('zoomOut') + '</button>' +
        '<output data-zval>100%</output>' +
        '<button class="tool tool--icon" type="button" data-zin title="Zoom in">' + SG.icon('zoomIn') + '</button>' +
      '</span>';

    $('[data-undo]', this.el.bar).addEventListener('click', function () { self.undo(); });
    $('[data-redo]', this.el.bar).addEventListener('click', function () { self.redo(); });
    $('[data-dupe]', this.el.bar).addEventListener('click', function () { self.duplicate(); });
    $('[data-del]', this.el.bar).addEventListener('click', function () { self.remove(); });
    $('[data-centre]', this.el.bar).addEventListener('click', function () { self.centre(); });
    $('[data-zin]', this.el.bar).addEventListener('click', function () { self.setZoom(self.zoom * 1.2); });
    $('[data-zout]', this.el.bar).addEventListener('click', function () { self.setZoom(self.zoom / 1.2); });
  };

  Editor.prototype.setZoom = function (z) {
    this.zoom = SG.clamp(z, 0.5, 3);
    this.el.scene.style.transform = 'scale(' + this.zoom + ')';
    $('[data-zval]', this.el.bar).textContent = Math.round(this.zoom * 100) + '%';
    this.syncHandles();
  };

  /* ================================================================ ACTIONS ==*/
  Editor.prototype.buildActions = function () {
    var self = this;
    var host = this.el.actions;
    if (!host) return;
    var rows = [];
    rows.push(this.onDone
      ? '<button class="btn btn--clay btn--block" type="button" data-done>' + SG.esc(this.doneLabel) + '</button>'
      : '<button class="btn btn--clay btn--block" type="button" data-generate>' + SG.icon('scan') + 'Generate print file</button>');
    if (this.onDone && this.mode === 'studio') {
      rows.push('<button class="btn btn--ghost btn--block" type="button" data-generate>' + SG.icon('scan') + 'Preview print file</button>');
    }
    rows.push('<button class="btn btn--ghost btn--block" type="button" data-reset>Start again</button>');
    host.innerHTML = rows.join('');
    var done = $('[data-done]', host);
    if (done) done.addEventListener('click', function () {
      self.generate().then(function (pkg) { self.onDone(pkg, self); });
    });
    var gen = $('[data-generate]', host);
    if (gen) gen.addEventListener('click', function () { self.showExport(); });
    var reset = $('[data-reset]', host);
    if (reset) reset.addEventListener('click', function () {
      self.design().layers = []; self.selected = null;
      self.draw(); self.refreshLayers(); self.refreshProps(); self.refreshSummary(); self.commit();
    });
  };

  Editor.prototype.bindAddButtons = function (root) {
    var self = this;
    var t = $('[data-add-text]', root), p = $('[data-add-photo]', root);
    if (t) t.addEventListener('click', function () { self.addText('Your words here'); });
    if (p) p.addEventListener('click', function () {
      var pane = self.pane('photo');
      var input = pane && $('[data-file]', pane);
      if (input) input.click();
    });
  };

  /* ================================================================== MOCK ==*/
  Editor.prototype.refreshMock = function () {
    var src = SG.mockupSrc(this.product.handle);
    var label = this.product.title + ' · studio mockup';
    this.el.mockmedia.innerHTML = SG.media('generic.mockup', {
      src: src, label: label, size: '1600 × 1600', className: 'media--fill'
    });
    this.el.mock.style.setProperty('--mock-radius', (this.product.mock && this.product.mock.radius) || '4px');
    this.el.print.style.setProperty('--art-bg', this.design().bg);

    var s = this.surface();
    var a = s.area;
    this.el.print.style.left = (a.x * 100) + '%';
    this.el.print.style.top = (a.y * 100) + '%';
    this.el.print.style.width = (a.w * 100) + '%';
    this.el.print.style.height = (a.h * 100) + '%';
    this.el.print.style.borderRadius = s.shape === 'round' ? '50%' : ((s.radius || 0) * 100 + '%');

    var g = this.el.guides;
    g.style.left = this.el.print.style.left;
    g.style.top = this.el.print.style.top;
    g.style.width = this.el.print.style.width;
    g.style.height = this.el.print.style.height;
    g.style.right = 'auto'; g.style.bottom = 'auto';
    $('.guide-safe', g).style.inset = (s.safe * 100) + '%';

    this.el.handles.style.left = this.el.print.style.left;
    this.el.handles.style.top = this.el.print.style.top;
    this.el.handles.style.width = this.el.print.style.width;
    this.el.handles.style.height = this.el.print.style.height;
    this.el.handles.style.right = 'auto'; this.el.handles.style.bottom = 'auto';
  };

  /* Fit the mockup inside the viewport. */
  Editor.prototype.fit = function () {
    var vp = this.el.viewport;
    if (!vp) return;
    var pad = 2 * parseFloat(getComputedStyle(vp).paddingLeft || 0);
    var availW = vp.clientWidth - pad;
    var availH = vp.clientHeight - pad;
    if (availW <= 0 || availH <= 0) return;
    var ratio = (this.product.mock && this.product.mock.ratio) || 1;   // w / h
    var w = availW, h = w / ratio;
    if (h > availH) { h = availH; w = h * ratio; }
    this.el.mock.style.width = Math.floor(w) + 'px';
    this.el.mock.style.height = Math.floor(h) + 'px';
    this.sizeCanvas();
    this.draw();
    this.syncHandles();
  };

  Editor.prototype.sizeCanvas = function () {
    var r = this.el.print.getBoundingClientRect();
    var dpr = Math.min(global.devicePixelRatio || 1, 2);
    var w = Math.max(1, Math.round(r.width)), h = Math.max(1, Math.round(r.height));
    this.el.canvas.width = Math.round(w * dpr);
    this.el.canvas.height = Math.round(h * dpr);
    this.el.canvas.style.width = '100%';
    this.el.canvas.style.height = '100%';
    this.dispW = w; this.dispH = h; this.dpr = dpr;
  };

  /* ================================================================ LAYERS ==*/
  Editor.prototype.addFiles = function (files) {
    var self = this;
    var list = Array.prototype.slice.call(files || []).filter(function (f) { return /^image\//.test(f.type); });
    if (!list.length) return;
    list.slice(0, 6).forEach(function (file) {
      var reader = new FileReader();
      reader.onload = function (e) {
        loadImage(e.target.result).then(function (img) {
          self.addImage(img, e.target.result, file.name);
        }).catch(function () { SG.toast('That image could not be read.', 'alert'); });
      };
      reader.readAsDataURL(file);
    });
  };

  Editor.prototype.addImage = function (img, src, name) {
    var s = this.surface();
    var printAspect = (s.widthMM / s.heightMM);
    var imgAspect = img.naturalWidth / img.naturalHeight;
    /* Default to covering the print area — the expected result for a photo
       product. "Fit" is one click away in the photo properties. */
    var w, h;
    if (imgAspect > printAspect) { h = 1; w = h * (imgAspect / printAspect); }
    else { w = 1; h = w * (printAspect / imgAspect); }
    var layer = {
      id: nowId(), type: 'image', name: name || 'Photograph',
      x: 0.5, y: 0.5, w: w, h: h, rot: 0, opacity: 1,
      src: src, img: img, filter: 'none',
      nw: img.naturalWidth, nh: img.naturalHeight
    };
    this.layers().push(layer);
    this.selected = layer.id;
    this.draw(); this.refreshLayers(); this.refreshProps(); this.refreshSummary(); this.syncHandles(); this.commit();
    SG.emit('editor:change', this);
  };

  Editor.prototype.addText = function (text) {
    var layer = {
      id: nowId(), type: 'text', name: (text || 'Text').split('\n')[0].slice(0, 22),
      x: 0.5, y: 0.5, rot: 0, opacity: 1,
      text: text || 'Your words here',
      font: this.defaultFont || 'cormorant',
      size: 0.12,
      color: this.defaultInk || '#1C1815',
      align: 'center',
      tracking: 0.02,
      leading: 1.16
    };
    this.layers().push(layer);
    this.selected = layer.id;
    this.draw(); this.refreshLayers(); this.refreshProps(); this.refreshSummary(); this.syncHandles(); this.commit();
    SG.emit('editor:change', this);
  };

  Editor.prototype.selectedLayer = function () {
    var id = this.selected, L = this.layers();
    for (var i = 0; i < L.length; i++) if (L[i].id === id) return L[i];
    return null;
  };

  Editor.prototype.select = function (id) {
    this.selected = id;
    this.refreshLayers(); this.refreshProps(); this.syncHandles();
  };

  Editor.prototype.remove = function () {
    var l = this.selectedLayer(); if (!l) return;
    this.design().layers = this.layers().filter(function (x) { return x.id !== l.id; });
    this.selected = null;
    this.draw(); this.refreshLayers(); this.refreshProps(); this.refreshSummary(); this.syncHandles(); this.commit();
  };

  Editor.prototype.duplicate = function () {
    var l = this.selectedLayer(); if (!l) return;
    var c = Object.assign({}, l, { id: nowId(), x: SG.clamp(l.x + 0.05, 0, 1), y: SG.clamp(l.y + 0.05, 0, 1) });
    this.layers().push(c); this.selected = c.id;
    this.draw(); this.refreshLayers(); this.refreshProps(); this.syncHandles(); this.commit();
  };

  Editor.prototype.centre = function () {
    var l = this.selectedLayer(); if (!l) return;
    l.x = 0.5; l.y = 0.5;
    this.draw(); this.syncHandles(); this.commit();
  };

  Editor.prototype.move = function (id, dir) {
    var L = this.layers();
    var i = L.findIndex(function (x) { return x.id === id; });
    if (i < 0) return;
    var j = SG.clamp(i + dir, 0, L.length - 1);
    L.splice(j, 0, L.splice(i, 1)[0]);
    this.draw(); this.refreshLayers(); this.commit();
  };

  /* ============================================================= TEMPLATES ==*/
  Editor.prototype.applyTemplate = function (id) {
    var L = this.layers();
    var imgs = L.filter(function (l) { return l.type === 'image'; });
    var txts = L.filter(function (l) { return l.type === 'text'; });
    var s = this.surface();
    var printAspect = s.widthMM / s.heightMM;

    function coverImage(l, w, h, cx, cy) {
      var ia = l.nw / l.nh;
      var boxA = (w / h) * printAspect;
      if (ia > boxA) { l.h = h; l.w = h * (ia / printAspect); }
      else { l.w = w; l.h = w * (printAspect / ia); }
      l.x = cx; l.y = cy; l.rot = 0;
    }

    if (id === 'full') {
      imgs.forEach(function (l) { coverImage(l, 1, 1, .5, .5); });
      txts.forEach(function (l, i) { l.x = .5; l.y = .84 + i * .08; l.rot = 0; });
    } else if (id === 'inset') {
      imgs.forEach(function (l) { coverImage(l, .76, .76, .5, .44); });
      txts.forEach(function (l, i) { l.x = .5; l.y = .88 + i * .07; l.rot = 0; });
    } else if (id === 'caption') {
      imgs.forEach(function (l) { coverImage(l, .84, .6, .5, .36); });
      txts.forEach(function (l, i) { l.x = .5; l.y = .76 + i * .1; l.rot = 0; });
    } else if (id === 'band') {
      imgs.forEach(function (l) { coverImage(l, 1, 1, .5, .5); });
      txts.forEach(function (l, i) { l.x = .5; l.y = .8 + i * .09; l.rot = 0; l.color = '#FFFFFF'; });
    } else if (id === 'stack') {
      imgs.forEach(function (l) { l.opacity = 0.18; coverImage(l, 1, 1, .5, .5); });
      txts.forEach(function (l, i) { l.x = .5; l.y = .42 + i * .16; l.rot = 0; });
    } else if (id === 'duo') {
      imgs.forEach(function (l) { coverImage(l, .5, 1, .25, .5); });
      txts.forEach(function (l, i) { l.x = .74; l.y = .42 + i * .14; l.rot = 0; });
    }
    $$('[data-tmpl]', this.pane('layout')).forEach(function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-tmpl') === id);
    });
    this.draw(); this.syncHandles(); this.commit();
  };

  /* ================================================================= DRAW ===*/
  Editor.prototype.measureText = function (ctx, l, W, H) {
    var fontPx = l.size * H;
    ctx.font = '400 ' + fontPx + 'px ' + fontCss(l.font);
    var lines = String(l.text || '').split('\n');
    var track = (l.tracking || 0) * fontPx;
    var widest = 0;
    lines.forEach(function (t) {
      var w = ctx.measureText(t).width + Math.max(0, t.length - 1) * track;
      if (w > widest) widest = w;
    });
    var lh = fontPx * (l.leading || 1.16);
    return { w: widest, h: lh * lines.length, lines: lines, fontPx: fontPx, lh: lh, track: track };
  };

  Editor.prototype.drawScene = function (ctx, W, H, forPrint) {
    var self = this;
    var d = this.design();
    ctx.save();
    ctx.fillStyle = d.bg || '#FFFFFF';
    ctx.fillRect(0, 0, W, H);
    ctx.restore();

    d.layers.forEach(function (l) {
      ctx.save();
      ctx.globalAlpha = l.opacity == null ? 1 : l.opacity;
      ctx.translate(l.x * W, l.y * H);
      if (l.rot) ctx.rotate(rad(l.rot));

      if (l.type === 'image' && l.img) {
        var w = l.w * W, h = l.h * H;
        if (l.flip) ctx.scale(-1, 1);
        if (ctx.filter !== undefined) ctx.filter = filterCss(l.filter);
        try { ctx.drawImage(l.img, -w / 2, -h / 2, w, h); } catch (e) {}
        if (ctx.filter !== undefined) ctx.filter = 'none';
      } else if (l.type === 'text') {
        var m = self.measureText(ctx, l, W, H);
        ctx.fillStyle = l.color || '#1C1815';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        var startY = -m.h / 2 + m.lh / 2;
        m.lines.forEach(function (line, i) {
          var y = startY + i * m.lh;
          if (m.track && ctx.letterSpacing !== undefined) {
            ctx.letterSpacing = m.track + 'px';
            ctx.fillText(line, 0, y);
            ctx.letterSpacing = '0px';
          } else if (m.track) {
            /* manual tracking fallback */
            var total = ctx.measureText(line).width + Math.max(0, line.length - 1) * m.track;
            var cx = -total / 2;
            for (var c = 0; c < line.length; c++) {
              var ch = line[c];
              var cw = ctx.measureText(ch).width;
              ctx.textAlign = 'left';
              ctx.fillText(ch, cx, y);
              cx += cw + m.track;
            }
            ctx.textAlign = 'center';
          } else {
            ctx.fillText(line, 0, y);
          }
        });
      }
      ctx.restore();
    });
  };

  Editor.prototype.draw = function () {
    if (!this.ctx || !this.dispW) return;
    var ctx = this.ctx;
    ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    ctx.clearRect(0, 0, this.dispW, this.dispH);
    this.drawScene(ctx, this.dispW, this.dispH, false);
    var has = this.layers().length > 0;
    if (this.el.empty) this.el.empty.classList.toggle('is-hidden', has);
    this.onChange(this);
  };

  /* ============================================================== GEOMETRY ==*/
  Editor.prototype.boundsOf = function (l) {
    var W = this.dispW, H = this.dispH;
    if (l.type === 'image') return { w: l.w * W, h: l.h * H };
    var m = this.measureText(this.ctx, l, W, H);
    return { w: m.w, h: m.h };
  };

  Editor.prototype.hit = function (px, py) {
    var W = this.dispW, H = this.dispH;
    var L = this.layers();
    for (var i = L.length - 1; i >= 0; i--) {
      var l = L[i];
      var b = this.boundsOf(l);
      var dx = px - l.x * W, dy = py - l.y * H;
      var a = rad(-(l.rot || 0));
      var rx = dx * Math.cos(a) - dy * Math.sin(a);
      var ry = dx * Math.sin(a) + dy * Math.cos(a);
      if (Math.abs(rx) <= b.w / 2 + 2 && Math.abs(ry) <= b.h / 2 + 2) return l;
    }
    return null;
  };

  /* ============================================================== HANDLES ===*/
  Editor.prototype.syncHandles = function () {
    var host = this.el.handles;
    var l = this.selectedLayer();
    if (!l || !this.dispW) { host.innerHTML = ''; return; }
    var b = this.boundsOf(l);
    var left = l.x * this.dispW, top = l.y * this.dispH;
    if (!host.__box) {
      host.innerHTML =
        '<div class="selbox">' +
          '<span class="selbox__stem"></span>' +
          '<button class="selbox__rot" type="button" data-h="rot">' + SG.icon('rotate') + '</button>' +
          '<span class="selbox__h" data-h="nw"></span><span class="selbox__h" data-h="ne"></span>' +
          '<span class="selbox__h" data-h="se"></span><span class="selbox__h" data-h="sw"></span>' +
        '</div>';
      host.__box = $('.selbox', host);
      this.bindHandles(host.__box);
    }
    var box = host.__box;
    box.style.width = Math.max(8, b.w) + 'px';
    box.style.height = Math.max(8, b.h) + 'px';
    box.style.left = (left - b.w / 2) + 'px';
    box.style.top = (top - b.h / 2) + 'px';
    box.style.transform = 'rotate(' + (l.rot || 0) + 'deg)';
  };

  Editor.prototype.bindHandles = function (box) {
    var self = this;
    $$('[data-h]', box).forEach(function (h) {
      h.addEventListener('pointerdown', function (e) {
        e.preventDefault(); e.stopPropagation();
        var kind = h.getAttribute('data-h');
        var l = self.selectedLayer(); if (!l) return;
        var rect = self.el.print.getBoundingClientRect();
        var cx = rect.left + l.x * rect.width, cy = rect.top + l.y * rect.height;
        var start = { d: Math.hypot(e.clientX - cx, e.clientY - cy), a: Math.atan2(e.clientY - cy, e.clientX - cx) };
        var base = l.type === 'image' ? { w: l.w, h: l.h } : { size: l.size };
        var baseRot = l.rot || 0;
        h.setPointerCapture(e.pointerId);

        var mv = function (ev) {
          if (kind === 'rot') {
            var a = Math.atan2(ev.clientY - cy, ev.clientX - cx);
            var deg = baseRot + (a - start.a) * 180 / Math.PI;
            if (ev.shiftKey) deg = Math.round(deg / 15) * 15;
            l.rot = Math.round(deg * 10) / 10;
          } else {
            var d = Math.hypot(ev.clientX - cx, ev.clientY - cy);
            var k = SG.clamp(d / Math.max(1, start.d), 0.08, 12);
            if (l.type === 'image') { l.w = base.w * k; l.h = base.h * k; }
            else { l.size = SG.clamp(base.size * k, 0.02, 1.2); }
          }
          self.draw(); self.syncHandles();
        };
        var up = function () {
          global.removeEventListener('pointermove', mv);
          global.removeEventListener('pointerup', up);
          self.commit();
        };
        global.addEventListener('pointermove', mv);
        global.addEventListener('pointerup', up);
      });
    });
  };

  /* ================================================================= STAGE ==*/
  Editor.prototype.bindStage = function () {
    var self = this;
    var canvas = this.el.canvas;

    canvas.addEventListener('pointerdown', function (e) {
      var rect = self.el.print.getBoundingClientRect();
      var px = (e.clientX - rect.left) / rect.width * self.dispW;
      var py = (e.clientY - rect.top) / rect.height * self.dispH;
      var l = self.hit(px, py);
      self.select(l ? l.id : null);
      if (!l) return;

      canvas.setPointerCapture(e.pointerId);
      var ox = px / self.dispW - l.x, oy = py / self.dispH - l.y;
      var moved = false;

      var mv = function (ev) {
        var r = self.el.print.getBoundingClientRect();
        var nx = (ev.clientX - r.left) / r.width - ox;
        var ny = (ev.clientY - r.top) / r.height - oy;
        var snapped = { x: false, y: false };
        if (Math.abs(nx - 0.5) < 0.012) { nx = 0.5; snapped.x = true; }
        if (Math.abs(ny - 0.5) < 0.012) { ny = 0.5; snapped.y = true; }
        l.x = SG.clamp(nx, -0.6, 1.6);
        l.y = SG.clamp(ny, -0.6, 1.6);
        self.el.guides.classList.toggle('snap-y', snapped.x);
        self.el.guides.classList.toggle('snap-x', snapped.y);
        moved = true;
        self.draw(); self.syncHandles();
      };
      var up = function () {
        global.removeEventListener('pointermove', mv);
        global.removeEventListener('pointerup', up);
        self.el.guides.classList.remove('snap-x', 'snap-y');
        if (moved) self.commit();
      };
      global.addEventListener('pointermove', mv);
      global.addEventListener('pointerup', up);
    });

    canvas.addEventListener('dblclick', function () {
      var l = self.selectedLayer();
      if (l && l.type === 'text') {
        var ta = $('[data-prop-text]', self.el.props);
        if (ta) { ta.focus(); ta.select(); }
      }
    });

    /* drop a photo straight onto the canvas */
    ['dragenter', 'dragover'].forEach(function (t) {
      self.el.viewport.addEventListener(t, function (e) { e.preventDefault(); });
    });
    self.el.viewport.addEventListener('drop', function (e) {
      e.preventDefault();
      if (e.dataTransfer && e.dataTransfer.files) self.addFiles(e.dataTransfer.files);
    });

    /* keyboard */
    this._key = function (e) {
      if (!self.mount.isConnected) return;
      var tag = (document.activeElement || {}).tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      var l = self.selectedLayer();
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault(); if (e.shiftKey) self.redo(); else self.undo(); return;
      }
      if (!l) return;
      var step = e.shiftKey ? 0.02 : 0.004;
      if (e.key === 'Backspace' || e.key === 'Delete') { e.preventDefault(); self.remove(); }
      else if (e.key === 'ArrowLeft')  { e.preventDefault(); l.x -= step; self.draw(); self.syncHandles(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); l.x += step; self.draw(); self.syncHandles(); }
      else if (e.key === 'ArrowUp')    { e.preventDefault(); l.y -= step; self.draw(); self.syncHandles(); }
      else if (e.key === 'ArrowDown')  { e.preventDefault(); l.y += step; self.draw(); self.syncHandles(); }
    };
    document.addEventListener('keydown', this._key);

    if (this.el.layers) this.bindAddButtons(this.mount);
  };

  /* ============================================================== PANELS ====*/
  Editor.prototype.refreshLayers = function () {
    var self = this;
    var host = this.el.layers; if (!host) return;
    var L = this.layers();
    if (!L.length) { host.innerHTML = '<p class="layers__empty">Nothing added yet.<br>Upload a photo or add some words.</p>'; return; }
    host.innerHTML = L.slice().reverse().map(function (l) {
      var thumb = l.type === 'image'
        ? '<span class="layer__thumb"><img src="' + l.src + '" alt=""></span>'
        : '<span class="layer__thumb" style="font-family:' + fontCss(l.font) + '">Aa</span>';
      var name = l.type === 'image' ? (l.name || 'Photograph') : (String(l.text).split('\n')[0] || 'Text');
      return '<div class="layer' + (l.id === self.selected ? ' is-active' : '') + '" data-layer="' + l.id + '">' +
        thumb + '<span class="layer__name">' + SG.esc(name) + '</span>' +
        '<span class="layer__acts">' +
          '<button type="button" data-up="' + l.id + '" title="Bring forward">' + SG.icon('front') + '</button>' +
          '<button type="button" data-down="' + l.id + '" title="Send back">' + SG.icon('back') + '</button>' +
          '<button type="button" data-rm="' + l.id + '" title="Delete">' + SG.icon('trash') + '</button>' +
        '</span></div>';
    }).join('');

    $$('[data-layer]', host).forEach(function (n) {
      n.addEventListener('click', function (e) {
        if (e.target.closest('[data-up],[data-down],[data-rm]')) return;
        self.select(n.getAttribute('data-layer'));
      });
    });
    $$('[data-up]', host).forEach(function (b) { b.addEventListener('click', function () { self.move(b.getAttribute('data-up'), 1); }); });
    $$('[data-down]', host).forEach(function (b) { b.addEventListener('click', function () { self.move(b.getAttribute('data-down'), -1); }); });
    $$('[data-rm]', host).forEach(function (b) {
      b.addEventListener('click', function () { self.selected = b.getAttribute('data-rm'); self.remove(); });
    });
  };

  Editor.prototype.refreshProps = function () {
    var self = this;
    var host = this.el.props; if (!host) return;
    var l = this.selectedLayer();
    if (!l) {
      host.innerHTML = '<p class="props__empty">Select a photograph or a line of text to adjust it.<br><br>Drag to move · corner handles to resize · the top handle to rotate.</p>';
      return;
    }

    if (l.type === 'text') {
      host.innerHTML =
        '<div class="ctl"><span class="ctl__label">Words</span>' +
          '<textarea class="textarea" rows="3" data-prop-text>' + SG.esc(l.text) + '</textarea></div>' +
        '<div class="ctl"><span class="ctl__label">Typeface</span>' +
          '<select class="select" data-prop-font>' + FONTS.map(function (f) {
            return '<option value="' + f.id + '"' + (f.id === l.font ? ' selected' : '') + '>' + f.label + '</option>';
          }).join('') + '</select></div>' +
        '<div class="ctl"><span class="ctl__label">Size<b data-out="size">' + Math.round(l.size * 100) + '</b></span>' +
          '<input type="range" min="2" max="60" value="' + Math.round(l.size * 100) + '" data-prop-size></div>' +
        '<div class="ctl"><span class="ctl__label">Letter spacing<b data-out="track">' + Math.round(l.tracking * 100) + '</b></span>' +
          '<input type="range" min="-5" max="40" value="' + Math.round(l.tracking * 100) + '" data-prop-track></div>' +
        '<div class="ctl"><span class="ctl__label">Line height<b data-out="lead">' + l.leading.toFixed(2) + '</b></span>' +
          '<input type="range" min="80" max="220" value="' + Math.round(l.leading * 100) + '" data-prop-lead></div>' +
        '<div class="ctl"><span class="ctl__label">Rotation<b data-out="rot">' + Math.round(l.rot) + '°</b></span>' +
          '<input type="range" min="-180" max="180" value="' + Math.round(l.rot) + '" data-prop-rot></div>' +
        '<div class="ctl"><span class="ctl__label">Ink</span><div class="swatches" data-prop-ink></div></div>';

      $('[data-prop-text]', host).addEventListener('input', function (e) {
        l.text = e.target.value; self.draw(); self.syncHandles(); self.refreshLayers();
      });
      $('[data-prop-text]', host).addEventListener('change', function () { self.commit(); });
      $('[data-prop-font]', host).addEventListener('change', function (e) { l.font = e.target.value; self.draw(); self.syncHandles(); self.commit(); });
      bindRange($('[data-prop-size]', host), function (v) { l.size = v / 100; }, host, 'size', function (v) { return v; });
      bindRange($('[data-prop-track]', host), function (v) { l.tracking = v / 100; }, host, 'track', function (v) { return v; });
      bindRange($('[data-prop-lead]', host), function (v) { l.leading = v / 100; }, host, 'lead', function (v) { return (v / 100).toFixed(2); });
      bindRange($('[data-prop-rot]', host), function (v) { l.rot = v; }, host, 'rot', function (v) { return v + '°'; });
      this.paintSwatches($('[data-prop-ink]', host), INK_SWATCHES, l.color, function (c) { l.color = c; self.draw(); self.commit(); });

    } else {
      host.innerHTML =
        '<div class="ctl"><span class="ctl__label">Treatment</span>' +
          '<select class="select" data-prop-filter>' + FILTERS.map(function (f) {
            return '<option value="' + f.id + '"' + (f.id === l.filter ? ' selected' : '') + '>' + f.label + '</option>';
          }).join('') + '</select></div>' +
        '<div class="ctl"><span class="ctl__label">Scale<b data-out="scale">' + Math.round(l.w * 100) + '</b></span>' +
          '<input type="range" min="10" max="260" value="' + Math.round(l.w * 100) + '" data-prop-scale></div>' +
        '<div class="ctl"><span class="ctl__label">Rotation<b data-out="rot">' + Math.round(l.rot) + '°</b></span>' +
          '<input type="range" min="-180" max="180" value="' + Math.round(l.rot) + '" data-prop-rot></div>' +
        '<div class="ctl"><span class="ctl__label">Opacity<b data-out="op">' + Math.round(l.opacity * 100) + '</b></span>' +
          '<input type="range" min="8" max="100" value="' + Math.round(l.opacity * 100) + '" data-prop-op></div>' +
        '<div class="seg seg--icons" style="margin-bottom:.95rem">' +
          '<button type="button" data-fill title="Fill the print area">' + SG.icon('crop') + '</button>' +
          '<button type="button" data-fit title="Fit inside the print area">' + SG.icon('scan') + '</button>' +
          '<button type="button" data-flip title="Flip horizontally">' + SG.icon('flip') + '</button>' +
        '</div>' +
        '<div class="studio__hint" data-dpi></div>';

      $('[data-prop-filter]', host).addEventListener('change', function (e) { l.filter = e.target.value; self.draw(); self.commit(); });
      var ratio = l.h / l.w;
      bindRange($('[data-prop-scale]', host), function (v) { l.w = v / 100; l.h = l.w * ratio; }, host, 'scale', function (v) { return v; });
      bindRange($('[data-prop-rot]', host), function (v) { l.rot = v; }, host, 'rot', function (v) { return v + '°'; });
      bindRange($('[data-prop-op]', host), function (v) { l.opacity = v / 100; }, host, 'op', function (v) { return v; });
      $('[data-fill]', host).addEventListener('click', function () { self.fitImage(l, true); });
      $('[data-fit]', host).addEventListener('click', function () { self.fitImage(l, false); });
      $('[data-flip]', host).addEventListener('click', function () { l.rot = (l.rot || 0); l.flip = !l.flip; self.draw(); self.commit(); });
      this.updateDpiHint($('[data-dpi]', host), l);
    }

    function bindRange(input, set, host, out, fmt) {
      if (!input) return;
      input.addEventListener('input', function () {
        set(+input.value);
        var o = $('[data-out="' + out + '"]', host);
        if (o) o.textContent = fmt(+input.value);
        self.draw(); self.syncHandles();
      });
      input.addEventListener('change', function () { self.commit(); self.refreshSummary(); });
    }
  };

  Editor.prototype.fitImage = function (l, fill) {
    var s = this.surface();
    var printAspect = s.widthMM / s.heightMM;
    var ia = l.nw / l.nh;
    if (fill ? (ia > printAspect) : (ia < printAspect)) { l.h = 1; l.w = 1 * (ia / printAspect); }
    else { l.w = 1; l.h = 1 * (printAspect / ia); }
    l.x = 0.5; l.y = 0.5; l.rot = 0;
    this.draw(); this.syncHandles(); this.commit();
  };

  Editor.prototype.updateDpiHint = function (host, l) {
    if (!host) return;
    var s = this.surface();
    var inches = (l.w * s.widthMM) / 25.4;
    var dpi = Math.round(l.nw / Math.max(0.1, inches));
    var ok = dpi >= 150;
    host.innerHTML = (ok ? '' : '⚠ ') + 'Effective print resolution ' + dpi + ' DPI at this size' +
      (ok ? ' — sharp.' : ' — this may look soft. Try a larger original or scale the photo down.');
    host.style.color = ok ? '' : 'var(--clay-deep)';
  };

  /* Shows at a glance which faces already carry artwork — the difference
     between "I have done three months" and "I have done twelve". */
  Editor.prototype.markDesignedSurfaces = function () {
    var self = this;
    var pane = this.pane('layout');
    if (!pane) return;
    var done = 0;
    this.surfaces.forEach(function (s) {
      var has = (self.designs[s.id] || {}).layers && self.designs[s.id].layers.length > 0;
      if (has) done += 1;
      var btn = $('[data-surface="' + s.id + '"]', pane);
      if (btn) btn.classList.toggle('has-art', !!has);
    });
    var out = $('[data-surface-count]', pane);
    if (out && this.surfaces.length > 4) {
      out.textContent = done + ' of ' + this.surfaces.length + ' designed';
    }
  };

  /* =============================================================== SUMMARY ==*/
  Editor.prototype.warnings = function () {
    var self = this, out = [];
    var s = this.surface();
    this.layers().forEach(function (l) {
      if (l.type === 'image') {
        var inches = (l.w * s.widthMM) / 25.4;
        var dpi = Math.round(l.nw / Math.max(0.1, inches));
        if (dpi < 150) out.push('“' + (l.name || 'Photograph') + '” prints at about ' + dpi + ' DPI — it may look soft.');
      }
      if (!self.dispW || !self.ctx) return;
      var b = self.boundsOf(l);
      var halfW = b.w / (2 * (self.dispW || 1)), halfH = b.h / (2 * (self.dispH || 1));
      if (l.x - halfW < -0.02 || l.x + halfW > 1.02 || l.y - halfH < -0.02 || l.y + halfH > 1.02) {
        if (l.type === 'text') out.push('Some words fall outside the print area and will be trimmed.');
      }
    });
    return out.filter(function (v, i, a) { return a.indexOf(v) === i; });
  };

  Editor.prototype.refreshSummary = function () {
    var host = this.el.summary; if (!host) return;
    var s = this.surface();
    var px = { w: Math.round(s.widthMM / 25.4 * s.dpi), h: Math.round(s.heightMM / 25.4 * s.dpi) };
    var warn = this.warnings();
    host.innerHTML =
      '<div class="summary__row"><span>' + SG.esc(this.product.title) + '</span><strong data-price="' + this.product.price + '">' + SG.money(this.product.price) + '</strong></div>' +
      '<div class="summary__row"><span>Print area</span><strong>' + s.widthMM + ' × ' + s.heightMM + ' mm</strong></div>' +
      '<div class="summary__row"><span>Output</span><strong>' + px.w + ' × ' + px.h + ' px · ' + s.dpi + ' DPI</strong></div>' +
      (warn.length
        ? '<div class="summary__flag summary__flag--warn">' + SG.icon('alert') + '<span>' + SG.esc(warn[0]) + '</span></div>'
        : '<div class="summary__flag summary__flag--ok">' + SG.icon('check') + '<span>Artwork is print-ready.</span></div>');
  };

  /* ================================================================ HISTORY ==*/
  Editor.prototype.serialiseSurface = function () {
    var d = this.design();
    return {
      bg: d.bg,
      layers: d.layers.map(function (l) {
        var c = {}; Object.keys(l).forEach(function (k) { if (k !== 'img') c[k] = l[k]; });
        return c;
      })
    };
  };
  Editor.prototype.rehydrate = function (raw) {
    var l = Object.assign({}, raw);
    if (l.type === 'image' && l.src) {
      var img = new Image();
      img.src = l.src;
      l.img = img;
      var self = this;
      img.onload = function () { self.draw(); self.syncHandles(); };
    }
    return l;
  };

  Editor.prototype.snapshot = function () {
    var self = this;
    var out = { surfaceId: this.surfaceId, designs: {} };
    Object.keys(this.designs).forEach(function (k) {
      var prev = self.surfaceId; self.surfaceId = k;
      out.designs[k] = self.serialiseSurface();
      self.surfaceId = prev;
    });
    return out;
  };

  Editor.prototype.restore = function (snap) {
    var self = this;
    this.designs = {};
    Object.keys(snap.designs).forEach(function (k) {
      self.designs[k] = {
        bg: snap.designs[k].bg,
        layers: snap.designs[k].layers.map(function (l) { return self.rehydrate(l); })
      };
    });
    this.surfaceId = snap.surfaceId;
    this.selected = null;
    this.refreshMock(); this.draw(); this.refreshLayers(); this.refreshProps(); this.refreshSummary(); this.syncHandles();
  };

  Editor.prototype.commit = function () {
    this.history.push(this.snapshot());
    if (this.history.length > 40) this.history.shift();
    this.future = [];
    this.refreshSummary();
    this.markDesignedSurfaces();
    SG.emit('editor:change', this);
    this.onChange(this);
  };
  Editor.prototype.undo = function () {
    if (this.history.length < 2) return;
    this.future.push(this.history.pop());
    this.restore(this.history[this.history.length - 1]);
  };
  Editor.prototype.redo = function () {
    if (!this.future.length) return;
    var s = this.future.pop();
    this.history.push(s);
    this.restore(s);
  };

  /* ================================================================= LOAD ===*/
  Editor.prototype.load = function (pkg) {
    var self = this;
    if (!pkg || !pkg.surfaces) return;
    Object.keys(pkg.surfaces).forEach(function (id) {
      var s = pkg.surfaces[id];
      self.designs[id] = {
        bg: s.background || '#FFFFFF',
        layers: (s.layers || []).map(function (l) { return self.rehydrate(l); })
      };
    });
    if (pkg.options) this.options = Object.assign({}, pkg.options);
  };

  /* =============================================================== EXPORT ===*/
  Editor.prototype.renderSurface = function (id, maxPx) {
    var prev = this.surfaceId;
    this.surfaceId = id;
    var s = this.surface();
    var W = Math.round(s.widthMM / 25.4 * s.dpi);
    var H = Math.round(s.heightMM / 25.4 * s.dpi);
    if (maxPx && Math.max(W, H) > maxPx) {
      var k = maxPx / Math.max(W, H);
      W = Math.round(W * k); H = Math.round(H * k);
    }
    var c = document.createElement('canvas');
    c.width = W; c.height = H;
    var ctx = c.getContext('2d');
    this.drawScene(ctx, W, H, true);
    this.surfaceId = prev;
    return { canvas: c, w: W, h: H, mm: { w: s.widthMM, h: s.heightMM }, dpi: s.dpi };
  };

  /**
   * Build the print package. Full-resolution files are capped at 4000px on the
   * long edge in the browser preview build — raise `maxPx` (or render server
   * side) when wiring the live Printify upload.
   */
  Editor.prototype.generate = function (maxPx) {
    var self = this;
    return new Promise(function (resolve) {
      var out = {
        version: 1,
        product: self.product.handle,
        productTitle: self.product.title,
        market: SG.region,
        options: Object.assign({}, self.options),
        createdAt: new Date().toISOString(),
        surfaces: {},
        warnings: self.warnings(),
        shopify: SG.catalog.lineFor(self.product, SG.region, self.options)
      };
      self.surfaces.forEach(function (s) {
        var prev = self.surfaceId;
        self.surfaceId = s.id;
        var full = self.renderSurface(s.id, maxPx || 4000);
        var prev2 = self.renderSurface(s.id, 640);
        out.surfaces[s.id] = {
          id: s.id,
          label: s.label,
          widthMM: s.widthMM, heightMM: s.heightMM, dpi: s.dpi,
          /* The true print target. */
          px: { w: Math.round(s.widthMM / 25.4 * s.dpi), h: Math.round(s.heightMM / 25.4 * s.dpi) },
          /* What the browser actually rasterised — capped so a phone can cope.
             Production renders are re-rasterised server-side from `layers`. */
          renderedPx: { w: full.w, h: full.h },
          background: self.designs[s.id].bg,
          layers: self.designs[s.id].layers.map(function (l) {
            var c = {}; Object.keys(l).forEach(function (k) { if (k !== 'img') c[k] = l[k]; });
            return c;
          }),
          printDataUrl: full.canvas.toDataURL('image/png'),
          previewDataUrl: prev2.canvas.toDataURL('image/jpeg', 0.86)
        };
        self.surfaceId = prev;
      });
      out.preview = (out.surfaces[self.surfaces[0].id] || {}).previewDataUrl || '';
      resolve(out);
    });
  };

  Editor.prototype.showExport = function () {
    var self = this;
    var host = this.el.exportview;
    host.classList.add('is-on');
    host.innerHTML = '<div class="exportcard"><div class="exportcard__head">' + SG.icon('scan') +
      '<h4>Preparing your print file…</h4></div><div class="exportcard__body"><p class="studio__hint">Rendering at full resolution.</p></div></div>';

    this.generate().then(function (pkg) {
      var first = pkg.surfaces[Object.keys(pkg.surfaces)[0]];
      var spec = {
        product: pkg.product, market: pkg.market, options: pkg.options,
        shopify: pkg.shopify,
        surfaces: Object.keys(pkg.surfaces).map(function (k) {
          var s = pkg.surfaces[k];
          return { id: s.id, mm: [s.widthMM, s.heightMM], px: [s.px.w, s.px.h], dpi: s.dpi, layers: s.layers.length };
        }),
        warnings: pkg.warnings
      };
      host.innerHTML =
        '<div class="exportcard">' +
          '<div class="exportcard__head">' + SG.icon('check') + '<h4>Print file ready</h4></div>' +
          '<div class="exportcard__body">' +
            '<div class="exportcard__preview"><img src="' + first.previewDataUrl + '" alt="Design preview"></div>' +
            '<div class="exportcard__meta">' +
              Object.keys(pkg.surfaces).map(function (k) {
                var s = pkg.surfaces[k];
                return '<div><span>' + SG.esc(s.label) + '</span><b>' + s.px.w + ' × ' + s.px.h + ' px · ' + s.dpi + ' DPI</b></div>';
              }).join('') +
              '<div><span>Market</span><b>' + pkg.market + '</b></div>' +
              '<div><span>Browser render</span><b>' + first.renderedPx.w + ' × ' + first.renderedPx.h + ' px</b></div>' +
            '</div>' +
            '<p class="studio__hint">The browser rasterises a capped preview so phones can cope. The production file is re-rendered at full size from the layer data below when the order is sent to the print partner.</p>' +
            '<pre>' + SG.esc(JSON.stringify(spec, null, 2)) + '</pre>' +
          '</div>' +
          '<div class="exportcard__foot">' +
            '<a class="btn btn--sm" download="snapgoody-' + pkg.product + '.png" href="' + first.printDataUrl + '">' + SG.icon('download') + 'Download PNG</a>' +
            '<button class="btn btn--ghost btn--sm" type="button" data-close>Back to studio</button>' +
          '</div>' +
        '</div>';
      $('[data-close]', host).addEventListener('click', function () { host.classList.remove('is-on'); });
    });
  };

  Editor.prototype.destroy = function () {
    document.removeEventListener('keydown', this._key);
    if (this._resizeBound) global.removeEventListener('resize', this._resizeBound);
    SG.__editors = (SG.__editors || []).filter(function (e) { return e !== this; }, this);
    try { this.mount.innerHTML = ''; } catch (e) {}
  };

  SG.Editor = Editor;
}(window));
